import React from 'react';
import styled from 'styled-components';
import { Timezone } from '@bb/domain/dist/types';
import { Color, Link, Text, Title } from '@brokerbay/elements-web';
import {
  formatDateStrWithYearAndFullDayNoOrd,
  formatTimeStr,
  getTimezoneCode,
} from 'utils';
import { useBreakpoint } from 'hooks';
import ListingImage from './ListingImage';
import Space from '../Space';

export interface ListingEntryProps {
  line1: string;
  line2?: string;
  listingId: string;
  thumbnail?: string;
  time: string;
  timeEnd: string;
  timezone: Timezone;
  durationHour: number;
  durationMin: number;
  showTimezone?: boolean;
}

const ListingEntryV2: React.FC<ListingEntryProps> = ({
  line1,
  line2 = '',
  listingId,
  thumbnail,
  time,
  timeEnd,
  timezone,
  durationHour,
  durationMin,
  showTimezone = false,
}) => {
  const { sm } = useBreakpoint();
  const isMobile = !sm;
  const titleSize = isMobile ? 5 : 3;

  const listingAddress = `${line1} ${line2 || ''}`;
  const showingDate = formatDateStrWithYearAndFullDayNoOrd(time, timezone);
  const showingTime = `${formatTimeStr(
    time,
    timezone,
  )} - ${formatTimeStr(timeEnd, timezone)}`;
  const showingTimeZone =
    showTimezone && timezone && ` (${getTimezoneCode(timezone, time)})`;
  const showingDuration = `${durationHour === 0 ? '' : `${durationHour} h `}${durationMin === 0 ? '' : `${durationMin}  min`}`;

  return (
    <StyledSpace
      key={`${listingId}-${time}`}
      direction={isMobile ? 'vertical' : 'horizontal'}
      align="start"
      size={16}
      data-cy="listing-entry-main"
    >
      <ListingImage size="large" thumbnail={thumbnail} />
      <StyledTextContainer>
        <Link href={`/#/listing/${listingId}/view`} underlineOnHover>
          <StyledTitle level={titleSize} dataCy="listing-entry-link">
            {listingAddress}
          </StyledTitle>
        </Link>
        <StyledTimeInfoP size="large">{showingDate}</StyledTimeInfoP>
        <StyledTimeInfoP size="large">
          {showingTime}
          {showingTimeZone}
        </StyledTimeInfoP>
        <StyledTimeInfoP size="large">{showingDuration}</StyledTimeInfoP>
      </StyledTextContainer>
    </StyledSpace>
  );
};

export default ListingEntryV2;
const StyledSpace = styled(Space)`
  margin-top: 8px;
`;
const StyledTextContainer = styled.div`
  line-height: 1.5;
  display: flex;
  flex-direction: column;
`;
const StyledTitle = styled(Title)`
  white-space: wrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  && {
    margin-top: 2px;
    color: ${Color.BLACK};
    ${({ level }) => level === 5 && 'max-width:260px'}
  }
`;

const StyledTimeInfoP = styled(Text)`
  margin-bottom: 10px;
  &&:first-of-type {
    margin-top: 5px;
  }
  flex-grow: 1;
`;
